<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">Bookings</h5>

    <!-- Filter -->
    <div class="filter q-mb-md bg-gray q-pa-md">
      <div class="row items-center q-col-gutter-sm">
        <q-input
          v-model="search"
          class="col-sm-12 col-xl-4"
          label="Search"
          hint="Search by shipper, transporter, booking ID, container ID or company address"
          @keydown.enter="handleSearch"
        />

        <q-select
          v-model="city"
          emit-value
          map-options
          class="col-sm-6 col-xl-4"
          hint=""
          label="City"
          option-label="name"
          option-value="id"
          :options="cityList"
        />

        <q-select
          v-model="status"
          label="Status"
          class="col-sm-6 col-xl-4"
          hint=""
          :options="['All', 'Confirm', 'Completed']"
        />

        <q-input class="col-sm-6 col-xl-4" label="From" :value="fromComputed" @input="$refs.fromRef.hide()">
          <q-popup-proxy ref="fromRef" transition-show="scale" transition-hide="scale">
            <div>
              <q-date v-model="from" landscape @input="$refs.fromRef.hide()" />
            </div>
          </q-popup-proxy>

          <template #prepend>
            <q-icon name="event" color="primary" class="cursor-pointer" />
          </template>
        </q-input>

        <q-input class="col-sm-6 col-xl-4" label="To" :value="toComputed" @input="$refs.toRef.hide()">
          <q-popup-proxy ref="toRef" transition-show="scale" transition-hide="scale">
            <div>
              <q-date v-model="to" landscape @input="$refs.toRef.hide()" />
            </div>
          </q-popup-proxy>

          <template #prepend>
            <q-icon name="event" color="primary" class="cursor-pointer" />
          </template>
        </q-input>

        <div class="col-sm-6 col-xl-2">
          <q-btn class="full-width" flat="flat" color="primary" icon="cached" label="Reset" @click="resetSearch" />
        </div>

        <div class="col-sm-6 col-xl-2">
          <q-btn class="full-width" color="primary" icon="search" label="Search" @click="handleSearch" />
        </div>
      </div>
    </div>

    <!-- Data table -->
    <DataTable ref="table" wrap-cells :columns="columns">
      <template v-if="loading">
        <tr v-for="n in 10" :key="n">
          <td v-for="m in columns.length" :key="m">
            <q-skeleton type="text" />
          </td>
        </tr>
      </template>

      <tr v-else-if="!bookings.length">
        <td class="text-center" :colspan="columns.length">No records found.</td>
      </tr>

      <tr v-for="booking in bookings" v-else :key="booking.id">
        <!-- Booking ID -->
        <td>
          <span class="text-action" @click="openPdf(booking)">
            {{ booking.id | formatId }}
          </span>
        </td>

        <!-- Shipper Email/ID -->
        <td>
          <span class="text-action" @click="openUser(booking.request.shipper)">
            <span>{{ booking.request.shipper.email }}</span>
            <br />
            <span>{{ booking.request.shipper.id | formatId }}</span>
          </span>
        </td>

        <!-- Shipper business -->
        <td class="text-capitalize">{{ booking.request.shipper.businessName }}</td>

        <!-- Transporter Email/ID -->
        <td>
          <span class="text-action" @click="openUser(booking.transporter)">
            <span>{{ booking.transporter.email }}</span>
            <br />
            <span>{{ booking.company.id | formatId }}</span>
          </span>
        </td>

        <!-- Transporter trading name -->
        <td class="text-uppercase">
          {{ booking.company.tradingName }}
        </td>

        <!-- Deliver from/to -->
        <td>
          <p class="q-mb-xs">
            <strong class="q-mr-sm">FR:</strong
            >{{ getDeliverAddress(booking.request, booking.request.type === 'import', true) }}
          </p>
          <p class="q-mb-xs">
            <strong class="q-mr-sm">TO:</strong
            >{{ getDeliverAddress(booking.request, booking.request.type !== 'import', true) }}
          </p>
        </td>

        <!-- Containers -->
        <td>
          <strong>Total {{ booking.request.containers.length }}:</strong>
          <div v-for="container in booking.request.containers.slice(0, 5)" :key="container.id" class="q-mt-xs">
            {{ container.containerCode }}
            <p class="q-mb-sm text-muted">{{ container.type | formatSlugToName }}</p>
          </div>
          <p v-if="booking.request.containers.length > 5" class="q-mb-none">....</p>
        </td>

        <!-- Status -->
        <td class="text-weight-medium" :class="getStatusClass(booking.status)">
          {{ booking.status }}
        </td>

        <!-- Price -->
        <td>{{ booking.totalAud | formatCurrency }}</td>

        <!-- Vessel/Voyage -->
        <td class="extended text-center">
          {{ booking.request.vessel | formatVesVoy }} /
          {{ booking.request.voyage | formatVesVoy }}
        </td>

        <!-- Deliver type -->
        <td class="extended">
          <div>
            {{ booking.request.truckType | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Truck type</q-tooltip>
          </div>

          <div class="q-mt-xs">
            {{ booking.request.deliveryType[0] | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Delivery type</q-tooltip>
          </div>

          <div class="q-mt-xs">
            {{ booking.request.doorsPosition[0] | formatSlugToName }}
            <q-tooltip anchor="top middle" self="center middle">Door position</q-tooltip>
          </div>
        </td>

        <td>
          <div v-if="booking.invoice">
            <div class="text-action" @click="handleOpenInvoiceDialog(booking)">
              {{ booking.invoice.id | formatId }}
            </div>

            <div class="text-weight-medium">
              {{ booking.invoice.total | formatCurrency }}
            </div>
          </div>

          <div v-else class="text-center">N/A</div>
        </td>

        <!-- Accept booking time -->
        <td>
          <div>
            {{ booking.confirmedTime | formatDateTime }}
            <q-tooltip anchor="top middle" self="center middle">
              Shipper accept: {{ booking.acceptedTime | formatDateTime }}
            </q-tooltip>
          </div>
        </td>
      </tr>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <!-- RFQ Detail dialog -->
    <q-dialog v-if="selectedBooking" v-model="showPDF" @hide="selectedBooking = null">
      <q-card style="width: 50rem; max-width: 80vw; max-height: 85vh">
        <RFQDetail :id="selectedBooking.request.id" show-customer @back="showPDF = false" />
      </q-card>
    </q-dialog>

    <q-dialog v-if="selectedBooking" v-model="isShowInvoice" @hide="selectedBooking = null">
      <q-card style="width: 60rem; max-width: 80vw; max-height: 85vh">
        <q-card-section>
          <AdminBookingInvoice :booking="selectedBooking" @back="isShowInvoice = false" />
        </q-card-section>
      </q-card>
    </q-dialog>

    <RFQUserDialog v-if="selectedUser" :open.sync="showUser" :user.sync="selectedUser" />
  </Wrapper>
</template>

<script>
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import debounce from 'lodash/debounce';
import { getStatusClass, getDeliverAddress, getErrorMessage } from '@/utils';
import Wrapper from '@/components/Wrapper.vue';
import DataTable from '@/components/DataTableNew.vue';
import Pagination from '@/components/Pagination.vue';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import API from '@/apis';
import { DATE_FORMAT } from '@/utils/constants';

export default {
  name: 'AdminBookings',

  components: {
    Wrapper,
    DataTable,
    Pagination,
    RFQDetail: () => import('./Shared/RFQDetail.vue'),
    RFQUserDialog: () => import('./RFQDialog/RFQUserDialog.vue'),
    AdminBookingInvoice: () => import('./Bookings/AdminBookingInvoice.vue'),
  },

  mixins: [formatDateMixin],

  data() {
    return {
      loading: true,
      currentPage: 1,
      totalPages: 1,
      from: null,
      to: null,
      search: '',
      status: 'All',
      bookings: [],
      city: 'all',
      cityList: [],
      // For booking dialog
      showPDF: false,
      selectedBooking: null,
      // For user dialog
      showUser: false,
      selectedUser: null,
      company: null,
      isShowInvoice: false,
    };
  },

  computed: {
    columns() {
      return [
        'Booking ID',
        'Shipper Email - ID',
        'Shipper Name',
        'Transporter ID',
        'Transporter Company',
        'Deliver From - To',
        'Containers',
        'Status',
        'Price',
        'Vessel / Voyage',
        'Deliver Type',
        'Invoice',
        'Accept Booking Time',
        // additional col if need
        // 'Accepted Time',
      ];
    },
    fromComputed() {
      return this.from ? format(this.from, DATE_FORMAT) : '';
    },

    toComputed() {
      return this.to ? format(this.to, DATE_FORMAT) : '';
    },
  },

  watch: {
    currentPage() {
      this.debouncedSendRequest();

      window.scrollTo({
        top: this.$refs.table.$el.offsetTop,
        left: 0,
        behavior: 'smooth',
      });
    },
  },

  async created() {
    const [{ data }] = await Promise.all([this.$axios.request(API.city.getBasic()), this.sendRequest()]);

    this.cityList = [{ name: 'All', id: 'all' }, ...data.data];

    this.debouncedSendRequest = debounce(this.sendRequest, 300);
  },

  methods: {
    getStatusClass,
    getDeliverAddress,
    getParams() {
      const params = {};

      if (this.search) {
        params.search = this.search;
      }

      if (this.from) {
        params.from = parse(this.from).valueOf();
      }

      if (this.to) {
        params.to = parse(this.to).valueOf();
      }

      if (this.city !== 'all') {
        params.city = this.city;
      }

      if (this.status !== 'All') {
        params.status = this.status;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      return params;
    },
    async sendRequest() {
      this.loading = true;
      try {
        const params = this.getParams();
        const { data } = await this.$axios.request(API.admin.getBookings(params));

        this.bookings = data.data;
        this.currentPage = data.pagination.page;
        this.totalPages = data.pagination.pages;
      } catch (error) {
        this.$q.notify({
          message: getErrorMessage(error),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.sendRequest }],
        });
      } finally {
        this.loading = false;
      }
    },
    handleCloseDialog() {
      this.showShipper = false;
      this.showTransporter = false;
      this.showPDF = false;
      this.selectedBooking = {};
    },
    handleSearch() {
      this.currentPage = 1;
      this.debouncedSendRequest();
    },
    resetSearch() {
      this.search = '';
      this.city = 'all';
      this.status = 'All';
      this.from = null;
      this.to = null;
      this.currentPage = 1;

      this.debouncedSendRequest();
    },
    openPdf(booking) {
      this.selectedBooking = booking;
      this.showPDF = true;
    },
    openUser(user) {
      this.selectedUser = user;
      this.showUser = true;
    },
    handleOpenInvoiceDialog(booking) {
      this.selectedBooking = booking;
      this.isShowInvoice = true;
    },
  },
};
</script>

<style scoped>
.extended {
  min-width: 10rem;
}
</style>
